import { useMemo, useState } from 'react'
import { n } from '../format'
import { MAX_GROUP_DEPTH, countRules, isGroup, newGroup, newRule } from '../filterSpec'

const TEXT_OPS = new Set(['contains', 'not_contains', 'equals', 'not_equals', 'starts_with', 'ends_with', 'regex', 'in', 'not_in'])

function ValueEditor({ rule, column, arity, update }) {
  const [search, setSearch] = useState('')
  const options = useMemo(() => {
    if (!column?.distinct) return null
    const q = search.trim().toLowerCase()
    return q ? column.distinct.filter((d) => d.value.toLowerCase().includes(q)) : column.distinct
  }, [column, search])

  if (arity === 0) return null

  if (arity === 'list') {
    // Low-cardinality columns get real checkboxes; anything else takes a pasted list.
    if (!options) {
      return (
        <textarea
          className="input mono"
          rows={3}
          placeholder="One value per line"
          value={(rule.values ?? []).join('\n')}
          onChange={(e) => update({ values: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
        />
      )
    }
    const selected = new Set(rule.values ?? [])
    return (
      <div className="value-list">
        {column.distinct.length > 8 && (
          <input
            className="input tiny"
            placeholder="Find value…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
        <div className="value-options">
          {options.map((d) => (
            <label key={d.value} className="value-option">
              <input
                type="checkbox"
                checked={selected.has(d.value)}
                onChange={(e) => {
                  const next = new Set(selected)
                  if (e.target.checked) next.add(d.value)
                  else next.delete(d.value)
                  update({ values: [...next] })
                }}
              />
              <span className="value-text" title={d.value}>{d.value || '(empty)'}</span>
              <span className="value-count">{n(d.count)}</span>
            </label>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="value-row">
      <input
        className="input"
        placeholder={column?.kind === 'number' ? '0' : 'value'}
        value={rule.value ?? ''}
        onChange={(e) => update({ value: e.target.value })}
      />
      {arity === 2 && (
        <input
          className="input"
          placeholder="and"
          value={rule.value2 ?? ''}
          onChange={(e) => update({ value2: e.target.value })}
        />
      )}
    </div>
  )
}

function Combinator({ value, onChange, title }) {
  return (
    <div className="combinator" title={title}>
      {['AND', 'OR'].map((c) => (
        <button key={c} className={`seg ${value === c ? 'on' : ''}`} onClick={() => onChange(c)}>
          {c}
        </button>
      ))}
    </div>
  )
}

function RuleCard({ rule, join, ctx, onChange, onRemove }) {
  const { columns, operators, byName } = ctx
  const col = byName.get(rule.column)
  const ops = operators.filter((o) => o.kinds.includes(col?.kind ?? 'text'))
  const op = operators.find((o) => o.id === rule.op)

  const changeColumn = (name) => {
    const next = byName.get(name)
    const valid = operators.filter((o) => o.kinds.includes(next?.kind ?? 'text'))
    const keep = valid.some((o) => o.id === rule.op)
    onChange({ column: name, op: keep ? rule.op : (valid[0]?.id ?? 'equals'), values: [], value: '', value2: '' })
  }

  return (
    <div className="rule">
      <div className="rule-head">
        <span className="rule-join">{join}</span>
        <button className="icon-btn" title="Remove filter" onClick={onRemove}>
          ×
        </button>
      </div>
      <select className="input" value={rule.column} onChange={(e) => changeColumn(e.target.value)}>
        {columns.map((c) => (
          <option key={c.name} value={c.name}>
            {c.name}
            {c.kind === 'number' ? '  #' : ''}
          </option>
        ))}
      </select>
      <select className="input" value={rule.op} onChange={(e) => onChange({ op: e.target.value })}>
        {ops.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <ValueEditor rule={rule} column={col} arity={op?.arity ?? 1} update={onChange} />
      {op?.needs === 'email-check' && (
        <p className="hint tight">Uses the Emails tool’s domain check — run it on this column first.</p>
      )}
      {TEXT_OPS.has(rule.op) && (
        <label className="case-toggle">
          <input
            type="checkbox"
            checked={!!rule.caseSensitive}
            onChange={(e) => onChange({ caseSensitive: e.target.checked })}
          />
          match case
        </label>
      )}
    </div>
  )
}

/**
 * The children of one group, plus its add buttons. The root group is drawn flat
 * inside the panel (`depth === 0`); nested ones get a box around them from
 * GroupCard, so the shape of `A and (B or C)` is visible at a glance.
 */
function GroupBody({ group, depth, ctx, onChange, actions = null }) {
  const { columns, operators } = ctx
  const rules = group.rules

  const set = (rulesNext) => onChange({ ...group, rules: rulesNext })
  const replace = (id, next) => set(rules.map((r) => (r.id === id ? next : r)))
  const remove = (id) => set(rules.filter((r) => r.id !== id))
  const add = (node) => set([...rules, node])

  return (
    <>
      {rules.length === 0 && depth > 0 && (
        <p className="hint tight">Empty group — add a filter, or remove the group.</p>
      )}
      <div className="rules">
        {rules.map((node, i) => {
          const join = i === 0 ? (depth === 0 ? 'where' : '') : group.combinator.toLowerCase()
          return isGroup(node) ? (
            <GroupCard
              key={node.id}
              group={node}
              join={join}
              depth={depth + 1}
              ctx={ctx}
              onChange={(next) => replace(node.id, next)}
              onRemove={() => remove(node.id)}
            />
          ) : (
            <RuleCard
              key={node.id}
              rule={node}
              join={join}
              ctx={ctx}
              onChange={(patch) => replace(node.id, { ...node, ...patch })}
              onRemove={() => remove(node.id)}
            />
          )
        })}
      </div>
      <div className={depth === 0 ? 'panel-actions' : 'panel-actions tight group-add'}>
        <button className={`btn ${depth > 0 ? 'tiny' : ''}`} onClick={() => add(newRule(columns, operators))}>
          + {depth === 0 ? 'Add filter' : 'Filter'}
        </button>
        {depth < MAX_GROUP_DEPTH && (
          <button
            className={`btn ${depth > 0 ? 'tiny' : ''}`}
            title="A group is matched as a unit — use one for “… and (this or that)”"
            onClick={() => add(newGroup(columns, operators, group.combinator))}
          >
            + {depth === 0 ? 'Add group' : 'Group'}
          </button>
        )}
        {actions}
      </div>
    </>
  )
}

function GroupCard({ group, join, depth, ctx, onChange, onRemove }) {
  return (
    <div className="group">
      <div className="group-head">
        <span className="rule-join">{join}</span>
        <Combinator
          value={group.combinator}
          onChange={(c) => onChange({ ...group, combinator: c })}
          title={`Rows must match ${group.combinator === 'OR' ? 'any' : 'all'} of this group`}
        />
        <button className="icon-btn" title="Remove this group and its filters" onClick={onRemove}>
          ×
        </button>
      </div>
      <GroupBody group={group} depth={depth} ctx={ctx} onChange={onChange} />
    </div>
  )
}

export function FilterPanel({ columns, operators, draft, setDraft, onApply, dirty, applied }) {
  const byName = useMemo(() => new Map(columns.map((c) => [c.name, c])), [columns])
  const ctx = useMemo(() => ({ columns, operators, byName }), [columns, operators, byName])
  const total = countRules(draft)

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Filters</h2>
        {draft.rules.length > 1 && (
          <Combinator
            value={draft.combinator}
            onChange={(c) => setDraft({ ...draft, combinator: c })}
            title={`Rows must match ${draft.combinator === 'OR' ? 'any' : 'all'} of the top level`}
          />
        )}
      </header>

      {total === 0 && (
        <p className="hint">No filters — showing every row in the file. Add one to narrow it down.</p>
      )}

      <GroupBody
        group={draft}
        depth={0}
        ctx={ctx}
        onChange={setDraft}
        actions={
          <>
            <button className="btn primary" onClick={() => onApply()} disabled={!dirty}>
              {dirty ? 'Apply' : 'Applied'}
            </button>
            {(total > 0 || countRules(applied) > 0) && (
              <button
                className="btn subtle"
                onClick={() => {
                  const cleared = { combinator: 'AND', rules: [] }
                  setDraft(cleared)
                  onApply(cleared)
                }}
              >
                Clear
              </button>
            )}
          </>
        }
      />
    </section>
  )
}
